// Pure projection for the portfolio Financials lens.
// The server model (server/lib/projectHubFinancials.js) is authoritative:
// row values are project-record CapEx amounts in USD ex tax; totals cover
// Stage Active, financially tracked projects and are only shown when every
// tracked Active project has that metric. Unknown (null) is never zero.

export const PORTFOLIO_CURRENCY = "USD";

const num = (v) => (v == null || !["string", "number"].includes(typeof v) || (typeof v === "string" && !v.trim()) || !Number.isFinite(Number(v)) ? null : Number(v));
const diff = (a, b) => (a != null && b != null ? a - b : null);
export const ratioOf = (n, d) => (n != null && d != null && d > 0 ? n / d : null);

export const SORT_COLUMNS = [
  { key: "name", label: "Project", numeric: false },
  { key: "budget", label: "Budget", numeric: true },
  { key: "forecast", label: "Forecast", numeric: true },
  { key: "variance", label: "Variance", numeric: true },
  { key: "spent", label: "Spent (actuals)", numeric: true },
  { key: "spentPct", label: "Spent % of forecast", numeric: true },
  { key: "forecastVsBudget", label: "Forecast vs budget", numeric: true },
];
export const DEFAULT_SORT = { key: "budget", dir: "desc" };

export function portfolioRow(r) {
  const tracked = r.financialsTracked !== false;
  const budget = tracked ? num(r.budget) : null;
  const forecast = tracked ? num(r.forecast) : null;
  const spent = tracked ? num(r.spendToDate) : null;
  const variance = tracked ? (num(r.variance) ?? diff(budget, forecast)) : null;
  return {
    id: r.id, name: r.name || "Untitled project", stage: r.stage, tracked, issues: r.issues || {},
    budget, forecast, spent, variance,
    remaining: diff(forecast, spent),
    spentPct: ratioOf(spent, forecast),
    forecastVsBudget: ratioOf(forecast, budget),
  };
}

function totalFor(t) {
  if (!t || !(t.total > 0) || t.covered !== t.total) return { value: null, covered: t?.covered ?? 0, total: t?.total ?? 0, complete: false };
  const value = num(t.value);
  return { value, covered: t.covered, total: t.total, complete: value != null };
}

export function portfolioFinancials(data) {
  const rows = (Array.isArray(data?.rows) ? data.rows : []).map(portfolioRow);
  const t = data?.totals || {};
  const totals = { budget: totalFor(t.budget), forecast: totalFor(t.forecast), spent: totalFor(t.spendToDate), variance: totalFor(t.variance) };
  const active = rows.filter((r) => r.stage === "active");
  const trackedActive = active.filter((r) => r.tracked);
  const untracked = active.filter((r) => !r.tracked).map((r) => r.name);
  const B = totals.budget.value; const F = totals.forecast.value; const S = totals.spent.value;
  const summary = {
    budget: B, forecast: F, spent: S,
    variance: totals.variance.complete ? totals.variance.value : null,
    remaining: totals.forecast.complete && totals.spent.complete ? F - S : null,
    spentPct: totals.forecast.complete && totals.spent.complete ? ratioOf(S, F) : null,
    forecastVsBudget: totals.forecast.complete && totals.budget.complete ? ratioOf(F, B) : null,
  };
  return {
    rows, totals, summary,
    trackedCount: trackedActive.length, activeCount: active.length, untracked,
    bar: buildPortfolioBar(summary),
  };
}

export function buildPortfolioBar({ budget, forecast, spent }) {
  if (forecast == null) return { status: "unavailable", reason: "Portfolio forecast is incomplete, so it cannot be partitioned." };
  if (forecast <= 0) return { status: "unavailable", reason: "No positive portfolio forecast is recorded, so it cannot be partitioned." };
  if (spent == null) return { status: "unavailable", reason: "Portfolio actuals are incomplete, so the forecast cannot be partitioned." };
  if (budget == null || budget <= 0) return { status: "unavailable", reason: "No complete positive portfolio budget is recorded, so budget headroom cannot be shown." };
  if (spent < 0 || spent > forecast) return { status: "unavailable", reason: "Actuals exceed the forecast, so the parts cannot be shown as a partition." };
  const remaining = forecast - spent;
  if (budget >= forecast) {
    const headroom = budget - forecast;
    return {
      status: "ok", whole: budget, overBudget: null,
      segments: [
        { key: "spent", label: "Actuals spent", value: spent, share: spent / budget },
        { key: "remaining", label: "Remaining forecast", value: remaining, share: remaining / budget },
        { key: "headroom", label: "Under budget", value: headroom, share: headroom / budget },
      ],
    };
  }
  return {
    status: "ok", whole: forecast, overBudget: forecast - budget, budgetShare: budget / forecast,
    segments: [
      { key: "spent", label: "Actuals spent", value: spent, share: spent / forecast },
      { key: "remaining", label: "Remaining forecast", value: remaining, share: remaining / forecast },
    ],
  };
}

/** Stable sort; unknown/untracked always last regardless of direction; zero is a real value. */
export function sortPortfolioRows(rows, { key, dir } = DEFAULT_SORT) {
  const sign = dir === "asc" ? 1 : -1;
  return rows.map((r, i) => [r, i]).sort(([a, ia], [b, ib]) => {
    if (key === "name") {
      const c = String(a.name).localeCompare(String(b.name), "en", { sensitivity: "base" });
      return c ? c * sign : ia - ib;
    }
    const va = a[key]; const vb = b[key];
    if (va == null && vb == null) return ia - ib;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (va !== vb) return (va - vb) * sign;
    return ia - ib;
  }).map(([r]) => r);
}

export function coverageReason(field, total) {
  if (!total || total.total === 0) return "No tracked Active projects in this selection.";
  if (total.covered < total.total) return `${field} recorded for ${total.covered} of ${total.total} tracked Active projects; total withheld.`;
  return `${total.covered} of ${total.total} tracked Active projects.`;
}
